import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Box,
  FormControlLabel,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useChassis } from "../ChassisNoProvider/ChassisProvider";

interface ChecklistItem {
  chassisPrepItemName: string;
  specification: string;
  notes: string;
  quantity: string;
  status: boolean;
}

interface Props {
  chassisNo: string;
  setModel: (value: string) => void;
  model: string;
  isDisabled?: boolean;
  userRole?: string;
  setCheckedBy?: (value: string) => void;
  canAutoSetChassisNo?: boolean;
}

const ElectricalChassisPrepChecklist: React.FC<Props> = ({
  chassisNo,
  setModel,
  model,
  isDisabled = false,
  userRole = "",
  setCheckedBy,
  canAutoSetChassisNo,
}) => {
  const axiosPrivate = useAxiosPrivate();
  const { setChassisNo } = useChassis();
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [editableIndex, setEditableIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [checkedBy, setCheckedByLocal] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryChassisNo = params.get("chassisNo");
    if (queryChassisNo) {
      setChassisNo(queryChassisNo);
    }
  }, [location.search, setChassisNo]);

  const fetchData = useCallback(async () => {
    if (!chassisNo) return;
    setLoading(true);
    try {
      const response = await axiosPrivate.post(
        `prepare_chassis_prep/${chassisNo}`
      );
      const data = response.data;

      setModel(data.model || "");
      setCheckedByLocal(data.checkedBy || "");
      setDate(
        data.createdOn
          ? new Date(data.createdOn).toISOString().split("T")[0]
          : ""
      );

      const newChecklist = data.chassisPrepItems.map((item: any) => ({
        chassisPrepItemName: item.chassisPrepItemName || "",
        specification: item.specification || "",
        notes: item.notes || "",
        quantity: item.quantity || "",
        status: item.status || false,
      }));

      const applicableItems = newChecklist.filter(
        (item: { specification: string }) => item.specification !== "N/A"
      );
      const isComplete = applicableItems.every(
        (item: { status: any }) => item.status
      );

      setChecklist(newChecklist);
      setCompleteStatus(isComplete ? "Complete" : "Incomplete");
    } catch (error) {
      toast.error("Error fetching data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, chassisNo, setModel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveRework = useCallback(async () => {
    if (!checkedBy || checklist.length === 0) return;

    try {
      const reworkItems = checklist.map((item, index) => ({
        itemNo: index + 1,
        problemDescription: item.chassisPrepItemName,
        comment: "",
        whoFoundFault: checkedBy,
        responsible: "",
        whoDidRework: "",
        timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
        completed: false,
      }));

      await axiosPrivate.post(
        `rework/save`,
        {
          chassisNo,
          model,
          station_name: "qc_electrical_chassis_prep",
          reworkItems,
        },
        {
          params: { rework_type: "qc_electrical_chassis_prep" },
        }
      );

      console.log("Rework saved.");
    } catch (error) {
      console.error("Failed to save rework:", error);
    }
  }, [axiosPrivate, chassisNo, model, checklist, checkedBy]);

  useEffect(() => {
    if (checkedBy && checklist.length > 0) {
      saveRework();
    }
  }, [checkedBy, checklist, saveRework]);

  const handleChange = (index: number) => {
    setChecklist((prev) => {
      const updated = [...prev];
      updated[index].status = !updated[index].status;
      return updated;
    });
  };

  const handleCheckedByChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCheckedByLocal(event.target.value);
  };

  const handleBlur = () => setEditableIndex(null);

  const handleKeyPress = (event: React.KeyboardEvent, index: number) => {
    if (event.key === "Enter") {
      setEditableIndex(null);
    }
  };

  const handleNoteChange = (index: number, value: string) => {
    setChecklist((prev) =>
      prev.map((item, i) => (i === index ? { ...item, notes: value } : item))
    );
  };

  const handleCellClick = (index: number) => setEditableIndex(index);

  const saveData = async () => {
    setLoading(true);
    try {
      const applicableItems = checklist.filter(
        (item) => item.specification !== "N/A"
      );
      const isComplete = applicableItems.every((item) => item.status);

      const payload = {
        chassisNo,
        model,
        chassisPrepItems: checklist,
        checkedBy,
        createdOn: date,
        completeStatus: isComplete ? "Complete" : "Incomplete",
      };

      await toast.promise(
        axiosPrivate.post("create_or_update_qa_chassis_prep", payload),
        { pending: "Request pending" }
      );

      toast.success("Data Saved Successfully!");
      setCompleteStatus(isComplete ? "Complete" : "Incomplete");
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>

      <TableContainer
        sx={{
          position: "relative",
          filter: loading ? "blur(5px)" : "none",
          pointerEvents: loading ? "none" : "auto",
        }}
      >
        <Table aria-label="chassis prep checklist">
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "5%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "23%" }}>
                CHASSIS PREP ITEM
              </TableCell>
              <TableCell className="table__head_cell">SPECIFICATION</TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                NOTES
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "5%" }}>
                QTY
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "5%" }}>
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checklist.map((item, index) => {
              const serialNumber = index + 1;
              const isBatteryTorqueRow =
                item.chassisPrepItemName ===
                "Ensure each battery terminal is torqued to 27 Nm and apply a witness mark";

              return (
                <TableRow key={index} style={{ padding: "0px" }}>
                  <TableCell
                    sx={{
                      width: "60px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      padding: "4px",
                      borderLeft: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                    }}
                  >
                    {serialNumber}
                  </TableCell>
                  <TableCell
                    className="p-0 px-2"
                    sx={{ borderLeft: "1px solid #d0d0d0" }}
                  >
                    {item.chassisPrepItemName}
                  </TableCell>
                  <TableCell
                    className="p-0 px-2"
                    sx={{
                      borderLeft: "1px solid #d0d0d0",
                      backgroundColor: isBatteryTorqueRow
                        ? "#e0e0e0 !important"
                        : "inherit",
                    }}
                  >
                    {isBatteryTorqueRow
                      ? item.specification && item.specification !== "N/A"
                        ? item.specification
                        : ""
                      : item.specification}
                  </TableCell>
                  <TableCell
                    className="p-0 px-2"
                    onClick={() => handleCellClick(index)}
                    sx={{
                      cursor: "pointer",
                      borderLeft: "1px solid #d0d0d0",
                    }}
                  >
                    {editableIndex === index ? (
                      <TextField
                        variant="standard"
                        value={item.notes}
                        // disabled={isDisabled}
                        disabled={completeStatus === "Complete"}
                        onChange={(e) =>
                          handleNoteChange(index, e.target.value)
                        }
                        size="small"
                        fullWidth
                        multiline
                        onBlur={handleBlur}
                        onKeyPress={(e) => handleKeyPress(e, index)}
                        InputProps={{
                          disableUnderline: true,
                          style: {
                            fontSize: "14px",
                            fontWeight: "normal",
                          },
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "text.primary",
                          "&:hover": {
                            color: "primary.main",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {item.notes || ""}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    className="p-0 px-2"
                    sx={{
                      borderLeft: "1px solid #d0d0d0",
                      backgroundColor: isBatteryTorqueRow
                        ? "#e0e0e0 !important"
                        : "inherit",
                    }}
                  >
                    {isBatteryTorqueRow
                      ? item.quantity && item.quantity !== "N/A"
                        ? item.quantity
                        : ""
                      : item.quantity}
                  </TableCell>
                  <TableCell
                    className="p-0 px-2"
                    sx={{
                      borderLeft: "1px solid #d0d0d0",
                      textAlign: "center",
                      borderRight: "1px solid #d0d0d0",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={item.status === true}
                          onChange={() => handleChange(index)}
                          sx={{ margin: 0, padding: "3px" }}
                          disabled={completeStatus === "Complete"}
                          // disabled={isSaved || isDisabled}
                        />
                      }
                      label=""
                      sx={{ margin: 0 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked by:"
            variant="outlined"
            value={checkedBy}
            onChange={handleCheckedByChange}
            fullWidth
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date:"
            variant="outlined"
            type="date"
            value={date}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={loading || completeStatus === "Complete"}
      >
        Save
      </Button>
    </>
  );
};

export default ElectricalChassisPrepChecklist;
